/**
 * Fetches every page sequentially and merges item arrays (shared by *AllPages API helpers).
 *
 * Some APIs under-report `total_pages` (e.g. always 1). When `pageSize` is set, we keep
 * requesting the next page while the last chunk has a full page of rows so all pages are loaded.
 *
 * @param {object} options
 * @param {(page: number) => Promise<any>} options.fetchPage - 1-based page number
 * @param {(response: any) => number} options.getTotalPages - defaults to 1 if missing
 * @param {(response: any) => any[]|undefined|null} options.getItems
 * @param {number} [options.pageSize] - Request page size; required for overflow pagination
 * @returns {Promise<{ items: any[], total_pages: number }>}
 */
export async function collectPaginatedItems({
  fetchPage,
  getTotalPages,
  getItems,
  pageSize,
}) {
  const first = await fetchPage(1);
  let chunk = getItems(first) ?? [];
  const size =
    pageSize != null && pageSize > 0
      ? pageSize
      : chunk.length > 0
        ? chunk.length
        : 100;

  const allItems = [...chunk];
  let totalPages = Math.max(1, getTotalPages(first) ?? 1);

  for (let page = 2; page <= totalPages; page++) {
    const res = await fetchPage(page);
    chunk = getItems(res) ?? [];
    allItems.push(...chunk);
    const t = getTotalPages(res);
    if (t != null && t > totalPages) {
      totalPages = t;
    }
  }

  // Under-reported total_pages: last chunk is full-sized, so more pages likely exist
  const maxOverflowPages = 500;
  let nextPage = totalPages + 1;
  let overflowCount = 0;
  while (
    chunk.length === size &&
    size > 0 &&
    overflowCount < maxOverflowPages
  ) {
    const res = await fetchPage(nextPage);
    chunk = getItems(res) ?? [];
    if (!chunk.length) break;
    allItems.push(...chunk);
    const t = getTotalPages(res);
    if (t != null && t > totalPages) {
      totalPages = t;
    }
    nextPage++;
    overflowCount++;
    if (chunk.length < size) break;
  }

  return { items: allItems, total_pages: totalPages };
}
